import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

export function formatApiErrorDetail(detail) {
  if (!detail) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).filter(Boolean).join(" ");
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const api = {
  health: async () => {
    const response = await client.get("/");
    return response.data;
  },
  register: async (payload) => {
    const response = await client.post("/auth/register", payload);
    return response.data;
  },
  login: async (payload) => {
    const response = await client.post("/auth/login", payload);
    return response.data;
  },
  logout: async () => {
    const response = await client.post("/auth/logout");
    return response.data;
  },
  me: async () => {
    const response = await client.get("/auth/me");
    return response.data;
  },
  updateProfile: async (payload) => {
    const response = await client.put("/auth/me", payload);
    return response.data;
  },
  getAdminStudents: async () => {
    const response = await client.get("/admin/students");
    return response.data;
  },
  updateAdminStudent: async (userId, payload) => {
    const response = await client.patch(`/admin/students/${userId}`, payload);
    return response.data;
  },
  getTeacherGuides: async () => {
    const response = await client.get("/admin/teacher-guide");
    return response.data;
  },
  createTeacherGuide: async (payload) => {
    const response = await client.post("/admin/teacher-guide", payload);
    return response.data;
  },
  updateTeacherGuide: async (guideId, payload) => {
    const response = await client.put(`/admin/teacher-guide/${guideId}`, payload);
    return response.data;
  },
  deleteTeacherGuide: async (guideId) => {
    const response = await client.delete(`/admin/teacher-guide/${guideId}`);
    return response.data;
  },
  getScenarios: async () => {
    const response = await client.get("/scenarios");
    return response.data;
  },
  getScenario: async (scenarioId) => {
    const response = await client.get(`/scenarios/${scenarioId}`);
    return response.data;
  },
  getGrammarModules: async () => {
    const response = await client.get("/grammar/modules");
    return response.data;
  },
  getGrammarLesson: async (lessonId) => {
    const response = await client.get(`/grammar/lessons/${lessonId}`);
    return response.data;
  },
  submitGrammarAttempt: async (lessonId, answers, reviewConfirmed = false) => {
    const response = await client.post(`/grammar/lessons/${lessonId}/attempt`, { answers, review_confirmed: reviewConfirmed });
    return response.data;
  },
  getStudyModules: async () => {
    const response = await client.get("/study/modules");
    return response.data;
  },
  submitStudyAttempt: async (lessonId, answers, reviewConfirmed = false) => {
    const response = await client.post(`/study/lessons/${lessonId}/attempt`, { answers, review_confirmed: reviewConfirmed });
    return response.data;
  },
  getZeroLessons: async () => {
    const response = await client.get("/zero/lessons");
    return response.data;
  },
  submitZeroAttempt: async (lessonId, answers, reviewConfirmed = false) => {
    const response = await client.post(`/zero/lessons/${lessonId}/attempt`, { answers, review_confirmed: reviewConfirmed });
    return response.data;
  },
  getRecommendations: async () => {
    const response = await client.get("/recommendations");
    return response.data;
  },
  getUnlockDrills: async () => {
    const response = await client.get("/unlock/drills");
    return response.data;
  },
  getUnlockDiagnostic: async () => {
    const response = await client.get("/unlock/diagnostic/latest");
    return response.data;
  },
  submitUnlockDiagnostic: async (answers) => {
    const response = await client.post("/unlock/diagnostic", { answers });
    return response.data;
  },
  createUnlockSession: async (drillId) => {
    const response = await client.post("/unlock/sessions", { drill_id: drillId });
    return response.data;
  },
  submitUnlockAttempt: async (sessionId, responseText, elapsedSeconds) => {
    const response = await client.post(`/unlock/sessions/${sessionId}/attempt`, { response_text: responseText, elapsed_seconds: elapsedSeconds });
    return response.data;
  },
  getUnlockProgress: async () => {
    const response = await client.get("/unlock/progress");
    return response.data;
  },
  getAdminUnlockInsights: async () => {
    const response = await client.get("/admin/unlock/insights");
    return response.data;
  },
  getReviewItems: async () => {
    const response = await client.get("/review/items");
    return response.data;
  },
  answerReviewItem: async (itemId, answer) => {
    const response = await client.post(`/review/items/${encodeURIComponent(itemId)}/answer`, { answer });
    return response.data;
  },
  getReviewAnalytics: async () => {
    const response = await client.get("/review/analytics");
    return response.data;
  },
  createSession: async (scenarioId) => {
    const response = await client.post("/sessions", { scenario_id: scenarioId });
    return response.data;
  },
  sendTurn: async (sessionId, text) => {
    const response = await client.post(`/sessions/${sessionId}/turn`, { text });
    return response.data;
  },
  transcribeAudio: async (audioBlob) => {
    const formData = new FormData();
    const extension = audioBlob.type.includes("wav") ? "wav" : "webm";
    formData.append("audio", audioBlob, `speech.${extension}`);
    const response = await client.post("/transcribe", formData);
    return response.data;
  },
  completeSession: async (sessionId) => {
    const response = await client.post(`/sessions/${sessionId}/complete`);
    return response.data;
  },
  getSessions: async () => {
    const response = await client.get("/sessions");
    return response.data;
  },
  getSession: async (sessionId) => {
    const response = await client.get(`/sessions/${sessionId}`);
    return response.data;
  },
  getProgress: async () => {
    const response = await client.get("/progress");
    return response.data;
  },
};